import { useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useClinics, useAllClinics, useNearbyClinics, useAllNearbyClinics, useIslands, useServices } from '../hooks/useApi';
import ClinicMap from '../components/ClinicMap';
import ClinicCard from '../components/ClinicCard';
import ClinicFilters from '../components/ClinicFilters';
import SkeletonCard from '../components/SkeletonCard';
import { ChevronRight, ChevronLeft } from 'lucide-react';

const PAGE_SIZE = 12;

export default function ClinicsPage() {
  const [searchParams] = useSearchParams();
  const initialIsland = searchParams.get('island') || '';

  const [selectedIsland, setSelectedIsland] = useState(initialIsland);
  const [selectedService, setSelectedService] = useState('');
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [page, setPage] = useState(1);

  const { data: islands } = useIslands();
  const { data: services } = useServices();

  // All clinics for map pins (unpaginated)
  const { data: allIslandClinics } = useAllClinics(selectedIsland || undefined);
  const { data: allNearbyClinics } = useAllNearbyClinics(
    userLocation?.lat ?? 0, userLocation?.lng ?? 0, 10000, selectedService || undefined
  );
  const mapClinics = userLocation ? allNearbyClinics : allIslandClinics;

  const { data: clinicsByIsland, isLoading: isLoadingIsland } = useClinics(selectedIsland || undefined, page, PAGE_SIZE);
  const { data: nearbyClinics, isLoading: isLoadingNearby } = useNearbyClinics(
    userLocation?.lat ?? 0,
    userLocation?.lng ?? 0,
    10000,
    selectedService || undefined,
    page,
    PAGE_SIZE
  );

  const paginatedData = userLocation ? nearbyClinics : clinicsByIsland;
  const clinics = paginatedData?.items;
  const totalPages = paginatedData?.total_pages ?? 1;
  const total = paginatedData?.total ?? 0;
  const isLoading = userLocation ? isLoadingNearby : isLoadingIsland;

  const handleUseLocation = useCallback(() => {
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setSelectedIsland('');
        setPage(1);
        setLocationLoading(false);
      },
      () => setLocationLoading(false)
    );
  }, []);

  const handleIslandChange = useCallback((island: string) => {
    setSelectedIsland(island);
    setUserLocation(null);
    setPage(1);
  }, []);

  const mapCenter = userLocation
    ? { lat: userLocation.lat, lng: userLocation.lng }
    : { lat: 25.0343, lng: -77.3963 };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/">Home</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-navy font-medium">Find Clinics</span>
      </div>

      <h1 className="text-3xl md:text-4xl font-extrabold text-navy">Find a Clinic</h1>
      <p className="text-gray-400 mt-2 mb-8">Search by location, island, or service</p>

      <ClinicFilters
        islands={islands || []}
        services={services || []}
        selectedIsland={selectedIsland}
        selectedService={selectedService}
        locationLoading={locationLoading}
        onIslandChange={handleIslandChange}
        onServiceChange={setSelectedService}
        onUseLocation={handleUseLocation}
      />

      <div className="mt-6 rounded-xl overflow-hidden shadow-sm">
        <ClinicMap
          clinics={mapClinics || clinics || []}
          center={mapCenter}
          userLocation={userLocation}
        />
      </div>

      <div className="mt-8">
        {isLoading ? (
          <div role="status" aria-busy="true" aria-label="Loading clinics" className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <SkeletonCard count={6} />
          </div>
        ) : !clinics?.length ? (
          <div className="text-center py-12 text-gray-400">No clinics found</div>
        ) : (
          <>
            <p className="text-sm text-gray-400 mb-4">
              {total} clinic{total !== 1 ? 's' : ''} found
              {totalPages > 1 && <span> &middot; Page {page} of {totalPages}</span>}
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clinics.map((clinic) => (
                <ClinicCard key={clinic.id} clinic={clinic} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn btn-outline flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" /> Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .reduce<(number | '...')[]>((acc, p, i, arr) => {
                    if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === '...' ? (
                      <span key={`ellipsis-${i}`} className="px-2 text-gray-400">...</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`w-10 h-10 rounded-lg text-sm font-semibold transition-colors ${
                          p === page
                            ? 'bg-primary text-white'
                            : 'bg-white text-navy border border-border hover:bg-gray-50'
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="btn btn-outline flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
